import {
  CalendarCheck,
  LayoutGrid,
  Reply,
  Search,
  Settings,
  TrendingUp,
  Users,
  Zap,
} from "lucide-react";
import Logo from "./Logo";

const KPIS = [
  { label: "Qualified Leads", value: "1,284", delta: "+18.2%", icon: Users },
  { label: "Reply Rate", value: "42.6%", delta: "+6.1%", icon: Reply },
  { label: "Pipeline Value", value: "$86.4K", delta: "+23.4%", icon: TrendingUp },
  { label: "Meetings Booked", value: "37", delta: "+12.0%", icon: CalendarCheck },
];

const ACTIVITY = [
  { company: "Acme Corp", action: "Opened outreach sequence", time: "2m ago", dot: "bg-emerald-400" },
  { company: "NovaRetail", action: "Booked a demo call", time: "14m ago", dot: "bg-teal-400" },
  { company: "Brightline Labs", action: "Replied to follow-up", time: "31m ago", dot: "bg-blue-400" },
  { company: "Vertex Supply", action: "High-intent lead discovered", time: "48m ago", dot: "bg-amber-400" },
];

const BARS = [34, 52, 44, 66, 58, 78, 92];
const DAYS = ["M", "T", "W", "T", "F", "S", "S"];

export default function DashboardMock() {
  return (
    <div
      className="overflow-hidden rounded-2xl border border-white/10 bg-gradient-to-b from-[#0C0E22]/95 to-[#070818]/95 shadow-[0_40px_120px_rgba(0,0,0,0.6)] backdrop-blur-xl"
      data-testid="hero-dashboard-preview"
    >
      <div className="flex items-center gap-3 border-b border-white/5 px-5 py-3.5">
        <div className="flex gap-1.5">
          <span className="h-2.5 w-2.5 rounded-full bg-[#3A3E66]" />
          <span className="h-2.5 w-2.5 rounded-full bg-[#3A3E66]" />
          <span className="h-2.5 w-2.5 rounded-full bg-teal-500/70" />
        </div>
        <div className="mx-auto flex items-center gap-2 rounded-full border border-white/5 bg-white/5 px-4 py-1">
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-emerald-400" />
          <span className="text-[11px] font-medium tracking-wide text-slate-400">
            app.startxpowered.com
          </span>
        </div>
        <div className="w-14" />
      </div>

      <div className="flex">
        <div className="hidden w-14 flex-col items-center gap-5 border-r border-white/5 py-6 sm:flex">
          <Logo className="h-9 w-9" />
          <LayoutGrid className="h-4 w-4 text-teal-300" />
          <Search className="h-4 w-4 text-slate-600" />
          <Zap className="h-4 w-4 text-slate-600" />
          <Settings className="h-4 w-4 text-slate-600" />
        </div>

        <div className="flex-1 p-5 md:p-6">
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            {KPIS.map((kpi) => (
              <div
                key={kpi.label}
                className="rounded-xl border border-white/5 bg-white/[0.03] p-3.5"
              >
                <div className="flex items-center justify-between">
                  <kpi.icon className="h-3.5 w-3.5 text-teal-300/80" />
                  <span className="text-[10px] font-semibold text-emerald-400">
                    {kpi.delta}
                  </span>
                </div>
                <p className="font-display mt-2 text-xl font-semibold text-white md:text-2xl">
                  {kpi.value}
                </p>
                <p className="mt-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-500">
                  {kpi.label}
                </p>
              </div>
            ))}
          </div>

          <div className="mt-4 grid gap-4 lg:grid-cols-5">
            <div className="rounded-xl border border-white/5 bg-white/[0.03] p-4 lg:col-span-3">
              <div className="mb-3 flex items-center justify-between">
                <div>
                  <p className="text-xs font-semibold text-white">Pipeline Growth</p>
                  <p className="text-[10px] text-slate-500">Last 30 days &bull; AI-sourced</p>
                </div>
                <span className="rounded-full border border-teal-400/20 bg-teal-400/10 px-2.5 py-1 text-[10px] font-semibold text-teal-300">
                  Live
                </span>
              </div>
              <svg viewBox="0 0 560 190" className="h-36 w-full md:h-44" preserveAspectRatio="none">
                <defs>
                  <linearGradient id="areaFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#14B8A6" stopOpacity="0.35" />
                    <stop offset="100%" stopColor="#14B8A6" stopOpacity="0" />
                  </linearGradient>
                  <linearGradient id="lineStroke" x1="0" y1="0" x2="1" y2="0">
                    <stop offset="0%" stopColor="#60A5FA" />
                    <stop offset="55%" stopColor="#38BDF8" />
                    <stop offset="100%" stopColor="#22D3EE" />
                  </linearGradient>
                </defs>
                {[38, 76, 114, 152].map((y) => (
                  <line key={y} x1="0" y1={y} x2="560" y2={y} stroke="#FFFFFF" strokeOpacity="0.05" />
                ))}
                <path
                  d="M0,150 C50,138 80,142 120,118 C160,94 200,104 240,92 C280,80 310,88 350,64 C390,40 430,58 470,42 C510,26 540,34 560,24 L560,190 L0,190 Z"
                  fill="url(#areaFill)"
                />
                <path
                  d="M0,150 C50,138 80,142 120,118 C160,94 200,104 240,92 C280,80 310,88 350,64 C390,40 430,58 470,42 C510,26 540,34 560,24"
                  fill="none"
                  stroke="url(#lineStroke)"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                />
                <circle cx="470" cy="42" r="4" fill="#22D3EE" />
                <circle cx="470" cy="42" r="8" fill="#22D3EE" opacity="0.25" />
              </svg>
            </div>

            <div className="flex flex-col gap-4 lg:col-span-2">
              <div className="rounded-xl border border-white/5 bg-white/[0.03] p-4">
                <p className="mb-1 text-xs font-semibold text-white">Lead Activity</p>
                <p className="mb-3 text-[10px] text-slate-500">Real-time engagement feed</p>
                <div className="space-y-2.5">
                  {ACTIVITY.map((item, i) => (
                    <div
                      key={item.company}
                      className="feed-item flex items-center gap-2.5"
                      style={{ animationDelay: `${1.4 + i * 0.25}s` }}
                    >
                      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${item.dot}`} />
                      <div className="min-w-0 flex-1">
                        <p className="truncate text-[11px] font-semibold text-slate-200">
                          {item.company}
                        </p>
                        <p className="truncate text-[10px] text-slate-500">{item.action}</p>
                      </div>
                      <span className="shrink-0 text-[9px] text-slate-600">{item.time}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="rounded-xl border border-white/5 bg-white/[0.03] p-4">
                <div className="mb-2 flex items-center justify-between">
                  <p className="text-xs font-semibold text-white">Outreach This Week</p>
                  <span className="text-[10px] font-semibold text-emerald-400">+24%</span>
                </div>
                <div className="flex h-14 items-end gap-1.5">
                  {BARS.map((h, i) => (
                    <div key={i} className="flex flex-1 flex-col items-center gap-1">
                      <div
                        className={`w-full rounded-sm ${
                          i === BARS.length - 1
                            ? "bg-gradient-to-t from-blue-500 to-teal-400"
                            : "bg-cyan-400/25"
                        }`}
                        style={{ height: `${h}%` }}
                      />
                      <span className="text-[8px] text-slate-600">{DAYS[i]}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
